import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, InjectionToken } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/** Where the engine gate sends a user whose MSP now lives on the new app (v2). */
export interface EngineGateOnKraken {
    kind: 'on_kraken';
    krakenUrl: string;
}

/**
 * The MSP is being moved between engines (or the engine could not be read, which is treated the same: retry).
 * `direction` is null for `msp_engine_unavailable`, where no switch is known to be happening.
 */
export interface EngineGateSwitching {
    kind: 'switching';
    direction: 'to_v2' | 'to_v1' | null;
    retryAfterMs: number;
}

/**
 * What the backend's engine gate (`backend/src/graph-api/oauth/engine-gate.ts`) said about the token mint.
 * `null` means the mint is not refusing on engine grounds, so the app renders as normal.
 */
export type EngineGateState = EngineGateOnKraken | EngineGateSwitching | null;

/** Structural slice of `HttpErrorResponse`, so the classifier is unit-testable without Angular. */
export interface GateErrorLike {
    status: number;
    error?: unknown;
    headers?: { get(name: string): string | null };
}

/** Where the switch-back request has got to. `sent` is also what a reload restores from sessionStorage. */
export interface SwitchBackState {
    phase: 'idle' | 'working' | 'sent' | 'error';
    mspName: string | null;
}

/** Retry-After fallback: the backend always sends 15 s, this covers a proxy stripping the header. */
export const DEFAULT_RETRY_AFTER_MS = 15_000;
/** Longest wait between polls, so a bad header cannot park the page for minutes. */
export const MAX_RETRY_AFTER_MS = 60_000;

/**
 * The token mint, polled while the MSP is switching. It sits behind the engine gate but is the only call that
 * tells us the gate has opened again.
 */
export const GATE_POLL_URL = '/api/microsoft/oauth/token';

/**
 * Cookie-authenticated route that tells the Octiga team an MSP on the new app wants to go back. It is ahead of the
 * engine gate (the gate is on the token mint only), and the token interceptor must exempt it: attaching a token
 * would try to mint, and the mint is exactly what is refusing.
 */
export const SWITCH_BACK_URL = '/api/microsoft/oauth/switch-back-request';

/** sessionStorage key prefix: one remembered request per MSP, per browser session (the server does not dedupe). */
export const SWITCH_BACK_STORAGE_PREFIX = 'octiga.switch-back-request:';

/**
 * Reload hook. Every call failed while the MSP was switching, so a full reload is the clean "continue" once the
 * mint answers again; injectable so specs can assert it without navigating jsdom.
 */
export const ENGINE_GATE_RELOAD = new InjectionToken<() => void>('ENGINE_GATE_RELOAD', {
    providedIn: 'root',
    factory: () => () => window.location.reload(),
});

/**
 * Poll timer hook: schedules `callback` after `ms` and returns a canceller. Injectable so specs can drive it.
 * The default resolves `setTimeout` at call time, so Jest fake timers also work without overriding it.
 */
export const ENGINE_GATE_TIMER = new InjectionToken<(callback: () => void, ms: number) => () => void>(
    'ENGINE_GATE_TIMER',
    {
        providedIn: 'root',
        factory: () => (callback, ms) => {
            const handle = setTimeout(callback, ms);
            return () => clearTimeout(handle);
        },
    },
);

/** Parse a response body that may arrive as an object or as unparsed JSON text. */
function bodyOf(error: unknown): Record<string, unknown> | null {
    if (typeof error === 'string') {
        try {
            const parsed: unknown = JSON.parse(error);
            return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null;
        } catch {
            return null;
        }
    }
    return error && typeof error === 'object' ? (error as Record<string, unknown>) : null;
}

/** Retry-After in whole seconds -> milliseconds, defaulted when absent/garbled and capped at 60 s. */
function retryAfterMs(headers: GateErrorLike['headers']): number {
    const seconds = Number(headers?.get('Retry-After'));
    if (!Number.isFinite(seconds) || seconds <= 0) return DEFAULT_RETRY_AFTER_MS;
    return Math.min(Math.ceil(seconds * 1000), MAX_RETRY_AFTER_MS);
}

/**
 * Map a failed call to the engine-gate answer it carries, or null for any ordinary error.
 * Why: the gate answers with specific status + body codes (403 `msp_on_kraken`, 503 `msp_switching` /
 * `msp_engine_unavailable`); an unrelated 403/500 must never put the whole app behind the gate page.
 * @param error an `HttpErrorResponse` (or anything shaped like one)
 */
export function classifyEngineGateError(error: GateErrorLike): EngineGateState {
    const body = bodyOf(error?.error);
    const code = body?.['code'];
    if (error?.status === 403 && code === 'msp_on_kraken') {
        const krakenUrl = body?.['krakenUrl'];
        return { kind: 'on_kraken', krakenUrl: typeof krakenUrl === 'string' ? krakenUrl : '' };
    }
    if (error?.status === 503 && code === 'msp_switching') {
        const direction = body?.['direction'];
        return {
            kind: 'switching',
            direction: direction === 'to_v2' || direction === 'to_v1' ? direction : null,
            retryAfterMs: retryAfterMs(error.headers),
        };
    }
    if (error?.status === 503 && code === 'msp_engine_unavailable') {
        return { kind: 'switching', direction: null, retryAfterMs: retryAfterMs(error.headers) };
    }
    return null;
}

/**
 * Holds the engine-gate state for the classic app (KRAK-434): the token interceptor feeds it every failed mint,
 * the container shows the gate page while it is non-null, and while the MSP is switching it polls the mint until
 * the answer changes. Also owns the "request a switch back" call and its per-browser-session memory.
 */
@Injectable({ providedIn: 'root' })
export class EngineGateService {
    private readonly stateSubject = new BehaviorSubject<EngineGateState>(null);
    private readonly switchBackSubject = new BehaviorSubject<SwitchBackState>({ phase: 'idle', mspName: null });
    private cancelPoll: (() => void) | null = null;

    /** The gate state; null when the app should render normally. */
    readonly state$: Observable<EngineGateState> = this.stateSubject.asObservable();
    /** Progress of the switch-back request. */
    readonly switchBack$: Observable<SwitchBackState> = this.switchBackSubject.asObservable();

    constructor(
        private http: HttpClient,
        @Inject(ENGINE_GATE_RELOAD) private reload: () => void,
        @Inject(ENGINE_GATE_TIMER) private timer: (callback: () => void, ms: number) => () => void,
    ) {}

    /** The current gate state, synchronously. */
    get state(): EngineGateState {
        return this.stateSubject.value;
    }

    /**
     * Offer a failed call to the gate. Gate answers set the state (and start polling when switching); anything else
     * is ignored, so callers can pass every mint failure and still rethrow it unchanged.
     * @param error the failed HTTP call
     */
    observe(error: GateErrorLike): void {
        const next = classifyEngineGateError(error);
        if (!next) return;
        this.stateSubject.next(next);
        if (next.kind === 'on_kraken') {
            this.stopPolling();
        } else if (!this.cancelPoll) {
            this.schedulePoll(next.retryAfterMs);
        }
    }

    /**
     * Ask the Octiga team to move the MSP back to the classic version. The answer is remembered in sessionStorage
     * for this browser session, because the server does not dedupe repeat requests.
     * @param mspId the session's MSP, part of the storage key
     */
    requestSwitchBack(mspId: string): void {
        if (this.switchBackSubject.value.phase === 'working') return;
        this.switchBackSubject.next({ phase: 'working', mspName: null });
        this.http.post<{ mspName?: string; requestedAt?: string }>(SWITCH_BACK_URL, null).subscribe({
            next: (res) => {
                const mspName = res?.mspName || 'your organisation';
                this.remember(mspId, { mspName, requestedAt: res?.requestedAt ?? null });
                this.switchBackSubject.next({ phase: 'sent', mspName });
            },
            error: () => this.switchBackSubject.next({ phase: 'error', mspName: null }),
        });
    }

    /**
     * Read back a request already sent in this browser session, so a reload shows "Request sent" rather than the
     * button again. Does nothing when there is none or a request is already in flight.
     * @param mspId the session's MSP
     */
    restoreSwitchBack(mspId: string): void {
        if (!mspId || this.switchBackSubject.value.phase !== 'idle') return;
        try {
            const raw = sessionStorage.getItem(SWITCH_BACK_STORAGE_PREFIX + mspId);
            if (!raw) return;
            const stored = JSON.parse(raw) as { mspName?: string };
            this.switchBackSubject.next({ phase: 'sent', mspName: stored?.mspName || 'your organisation' });
        } catch {
            // Storage unavailable or garbled: show the button again, which is the safe answer.
        }
    }

    private remember(mspId: string, value: { mspName: string; requestedAt: string | null }): void {
        try {
            sessionStorage.setItem(SWITCH_BACK_STORAGE_PREFIX + mspId, JSON.stringify(value));
        } catch {
            // Private mode / quota: the request was still sent, it just will not survive a reload.
        }
    }

    private schedulePoll(ms: number): void {
        this.cancelPoll = this.timer(() => {
            this.cancelPoll = null;
            this.poll();
        }, ms);
    }

    private stopPolling(): void {
        this.cancelPoll?.();
        this.cancelPoll = null;
    }

    private poll(): void {
        this.http.get(GATE_POLL_URL).subscribe({
            // The mint answers again: the MSP is served here. Every call failed meanwhile, so reload.
            next: () => this.reload(),
            error: (error) => {
                const answer = classifyEngineGateError(error);
                if (answer?.kind === 'on_kraken') {
                    this.stateSubject.next(answer);
                    return;
                }
                if (answer?.kind === 'switching') this.stateSubject.next(answer);
                const current = this.stateSubject.value;
                // Any other error (network blip, bare 5xx) keeps polling at the last known cadence.
                this.schedulePoll(current?.kind === 'switching' ? current.retryAfterMs : DEFAULT_RETRY_AFTER_MS);
            },
        });
    }
}
